import { combineLatestWith, distinctUntilChanged, map, Observable } from 'rxjs';
import { PageDataMode, PageDataState } from '../page-data/page-data.types';
import {
  GridData,
  GridDataParams,
  GridDataSourceState,
} from './grid-data-source-state';
import { descComparator } from '../../util/comparators';
import {
  ColumnFilter,
  GridSort,
  SortDirection,
} from '../../table/components/table/table.types';
import { filterEq } from './grid-data.types';

export interface PageDataToGridDataConfig<TData> {
  /**
   * Filter predicates for specific values of filter names
   */
  filterChecks?: Record<string, (item: TData, filterValue: string) => boolean>;
  /**
   * Compare functions for specific values of sortBy
   */
  comparisons?: Record<string, (item1: TData, item2: TData) => number>;
}

export function pageDataToGridData<TData>(
  config?: PageDataToGridDataConfig<TData>,
): (
  pageDataState$: Observable<PageDataState<unknown, TData[]>>,
  gridDataParams$: Observable<GridDataParams>,
) => Observable<GridDataSourceState<TData>> {
  const checkFilter = (item: TData, filterName: string, filterValue: string) =>
    config?.filterChecks?.[filterName]?.(item, filterValue) ?? true;

  const filterData = (data: TData[], filter: ColumnFilter): TData[] =>
    checkFilter
      ? data.filter(item =>
          filter.every(filter =>
            checkFilter(item, filter.colId, filter.filter),
          ),
        )
      : data;
  const compareFnForSort = (
    sort: GridSort,
  ): ((item1: TData, item2: TData) => number) | null => {
    const { sortDirection } = sort;
    if (sortDirection === SortDirection.none) {
      return null;
    }
    const { sortBy } = sort;
    const comparison = config?.comparisons?.[sortBy];
    if (!comparison) {
      return null;
    }
    if (sortDirection === SortDirection.asc) {
      return comparison;
    }
    if (sortDirection === SortDirection.desc) {
      return descComparator(comparison);
    }
    return null;
  };
  const sortData = (data: TData[], sort: GridSort): TData[] => {
    const compareFn = compareFnForSort(sort);
    return compareFn ? data.sort(compareFn) : data;
  };
  const getGridData = (
    data: TData[],
    gridDataParams: GridDataParams,
  ): GridData<TData> => {
    const { filter, sort, page, pageSize } = gridDataParams;
    const offset = (page - 1) * pageSize;
    const limit = pageSize;
    const filteredData = filterData(data, filter);
    const sortedData = sortData(filteredData, sort);
    const rowData = sortedData.slice(offset, offset + limit);
    const rowCount = filteredData.length;
    return {
      rowCount,
      rowData,
    };
  };
  return (pageDataState$, gridDataParams$) =>
    pageDataState$.pipe(
      combineLatestWith(
        gridDataParams$.pipe(
          distinctUntilChanged(
            (previous, current) =>
              previous.page === current.page &&
              previous.pageSize === current.pageSize &&
              filterEq(previous.filter, current.filter) &&
              GridSort.eq(previous.sort, current.sort),
          ),
        ),
      ),
      map(([pageDataState, gridDataParams]): GridDataSourceState<TData> => {
        if (pageDataState.mode === PageDataMode.standBy) {
          return GridDataSourceState.standBy(gridDataParams);
        }
        if (pageDataState.mode === PageDataMode.loading) {
          return GridDataSourceState.loading(gridDataParams);
        }
        if (pageDataState.mode === PageDataMode.loaded) {
          const gridData = getGridData(pageDataState.data, gridDataParams);
          return GridDataSourceState.loaded(gridDataParams, gridData);
        }
        if (pageDataState.mode === PageDataMode.error) {
          return GridDataSourceState.error(gridDataParams);
        }
        const exhaustivenessCheck: never = pageDataState;
        return exhaustivenessCheck;
      }),
    );
}
